import type { Command, FETraceApi, InitOptions } from './types';

/**
 * Reads `data-*` attributes from the <script> tag that loaded the widget.
 * Returns null when the SDK was imported as an ES module (no script tag).
 */
export function readScriptOptions(doc: Document = document): Partial<InitOptions> | null {
  const script =
    (doc.currentScript as HTMLScriptElement | null) ??
    Array.from(doc.querySelectorAll<HTMLScriptElement>('script[data-key]')).find((s) =>
      /widget(?:@[\w.-]+)?\.js/.test(s.src),
    ) ??
    null;

  if (!script) return null;

  const d = script.dataset;
  if (!d.key) return null;

  const opts: Partial<InitOptions> = { key: d.key };
  if (d.env) opts.env = d.env;
  if (d.apiUrl) opts.apiUrl = d.apiUrl;
  if (d.position && ['bottom-left', 'bottom-right', 'bottom-center', 'left-edge', 'right-edge'].includes(d.position)) opts.position = d.position as InitOptions['position'];
  if (d.locale === 'tr' || d.locale === 'en') opts.locale = d.locale;
  if (d.launcher === 'false') opts.launcher = false;
  if (d.hideOnAuthPages === 'false') opts.hideOnAuthPages = false;
  if (d.hideOnPaths) opts.hideOnPaths = splitPaths(d.hideOnPaths);
  return opts;
}

/** `data-hide-on-paths="/checkout/*, /odeme"` → `['/checkout/*', '/odeme']`. */
export function splitPaths(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Drains a pre-load command queue (`window.FETrace = window.FETrace || []; FETrace.push([...])`)
 * into a real API object. Anything that is not an array-shaped command is ignored.
 */
export function drainQueue(queue: unknown, api: FETraceApi): void {
  if (!Array.isArray(queue)) return;
  for (const item of queue) {
    if (Array.isArray(item) && typeof item[0] === 'string') {
      api.push(item as Command);
    }
  }
}
